import React, { useState, useEffect } from 'react';
import { X, Loader2, FileText, File as FilePdf } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import ReportFormatSelector from './ReportFormatSelector';
import { jsPDF } from 'jspdf';
import { Database } from '../types/supabase';

interface ReportGeneratorProps {
  onClose: () => void;
}

type Agency = Database['public']['Tables']['agencies']['Row'];

interface ReportFormat {
  id: string;
  name: string;
  settings: {
    companyLogo: boolean;
    companyName: boolean;
    reportType: boolean;
    preparedBy: boolean;
    generatedOn: boolean;
    projectSnapshot: boolean;
    projectHighlights: boolean;
    tasksSnapshot: boolean;
    tasksHighlights: boolean;
    keyHighlights: boolean;
    achievements: boolean;
    upcomingActions: boolean;
    teamProductivity: boolean;
    additionalInfo: boolean;
    meetingMinutes: boolean;
  };
}

export default function ReportGenerator({ onClose }: ReportGeneratorProps) {
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [preparedBy, setPreparedBy] = useState('');
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'text'>('pdf');
  const [selectedFormat, setSelectedFormat] = useState<ReportFormat | null>(null);
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [keyHighlights, setKeyHighlights] = useState('');
  const [achievements, setAchievements] = useState('');
  const [upcomingActions, setUpcomingActions] = useState('');
  const [selectedMeetingNoteIds, setSelectedMeetingNoteIds] = useState<string[]>([]);
  const [meetingNotes, setMeetingNotes] = useState<{id: string; title: string; content: string; created_at: string}[]>([]);
  const [reportType, setReportType] = useState('weekly');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    fetchAgencies();
    // Set default prepared by name from profile
    fetchUserProfile();
    // Fetch meeting notes if needed
    fetchMeetingNotes();
  }, []);

  // Fetch meeting notes when selectedFormat changes
  useEffect(() => {
    if (selectedFormat?.settings.meetingMinutes) {
      fetchMeetingNotes();
    }
  }, [selectedFormat]);

  // Fetch meeting notes when selectedAgencyId changes
  useEffect(() => {
    if (selectedFormat?.settings.meetingMinutes && selectedAgencyId) {
      fetchMeetingNotes();
    }
  }, [selectedAgencyId]);

  const fetchAgencies = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, contact_first_name, contact_last_name, email, phone, logo_url, additional_info')
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to load agencies');
    }
  };

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setPreparedBy(`${profile.first_name || ''} ${profile.last_name || ''}`.trim());
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchMeetingNotes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = supabase
        .from('notes')
        .select('id, title, content, created_at');
      
      // Add filters
      query = query.eq('user_id', user.id)
                  .eq('category', 'meeting');
      
      // Filter by agency if one is selected
      if (selectedAgencyId) {
        query = query.eq('agency_id', selectedAgencyId);
      }
      
      // Order by created_at
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;

      if (error) throw error;
      setMeetingNotes(data || []);
      
      // Clear selected meeting notes if they're no longer available
      setSelectedMeetingNoteIds(prevSelected => 
        prevSelected.filter(id => data?.some(note => note.id === id))
      );
    } catch (error) {
      console.error('Error fetching meeting notes:', error);
    }
  };
  
  const handleGenerate = async () => {
    setLoading(true);
    try {
      if (!selectedAgencyId) {
        toast.error('Please select a customer');
        return;
      }

      // Generate report content
      const reportContent = await generateReportContent();
      
      // Download in selected format
      if (outputFormat === 'pdf') {
        await downloadPDF(reportContent);
      } else {
        downloadText(reportContent);
      }
      
      toast.success('Report generated successfully');
      onClose();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const generateReportContent = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get agency data
    const { data: agency } = await supabase
      .from('agencies')
      .select('*')
      .eq('id', selectedAgencyId)
      .single();

    // Get projects data
    const { data: projects } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', user.id)
      .eq('agency_name', agency?.name);

    // Get tasks data
    const { data: tasks } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', user.id)
      .eq('agency_id', selectedAgencyId);

    // Get task assignments for this agency's tasks
    const { data: taskAssignments } = await supabase
      .from('task_assignments')
      .select(`
        team_member_id,
        task_id
      `)
      .in('task_id', tasks?.map(t => t.id) || []);
    
    // Get team members assigned to this agency's tasks
    const teamMemberIds = [...new Set(taskAssignments?.map(ta => ta.team_member_id) || [])];
    
    // Get team member details
    const { data: teamMembers } = await supabase
      .from('team_members')
      .select('*');

    // Get meeting notes for this agency if meetingMinutes is enabled
    let meetingNotes = [];
    if (selectedFormat?.settings.meetingMinutes) {
      if (selectedMeetingNoteIds.length > 0) {
        // Get the specific selected meeting notes
        const { data: notes } = await supabase
          .from('notes')
          .select('*')
          .in('id', selectedMeetingNoteIds);
        
        if (notes && notes.length > 0) {
          meetingNotes = notes;
        }
      }
    }

    const sections: string[] = [];
    
    // Only add header information once
    sections.push(`${reportType.toUpperCase()} REPORT`);
    sections.push(`Generated on: ${new Date().toLocaleDateString()}\n`);

    // Customer Information
    if (agency) {
      sections.push('CUSTOMER INFORMATION');
      sections.push(`Customer: ${agency.name}`);
      sections.push(`Contact: ${agency.contact_first_name} ${agency.contact_last_name}`);
      sections.push('');
    }

    // Projects Overview
    if (projects?.length && selectedFormat?.settings.projectSnapshot) {
      sections.push('PROJECTS OVERVIEW');
      sections.push(`Total Projects: ${projects.length}`);
      sections.push('Status Breakdown:');
      const projectsByStatus = projects.reduce((acc: any, proj) => {
        acc[proj.status] = (acc[proj.status] || 0) + 1;
        return acc;
      }, {});
      Object.entries(projectsByStatus).forEach(([status, count]) => {
        sections.push(`- ${status}: ${count}`);
      });
      
      // Add project titles with their status
      sections.push('\nProjects:');
      projects.forEach(project => {
        sections.push(`- ${project.name} (Status: ${project.status})`);
      });
      
      sections.push('');
    }

    // Tasks Summary
    if (tasks?.length && selectedFormat?.settings.tasksSnapshot) {
      sections.push('TASKS SUMMARY');
      sections.push(`Total Tasks: ${tasks.length}`);
      sections.push('Priority Breakdown:');
      const tasksByPriority = tasks.reduce((acc: any, task) => {
        acc[task.priority] = (acc[task.priority] || 0) + 1;
        return acc;
      }, {});
      Object.entries(tasksByPriority).forEach(([priority, count]) => {
        sections.push(`- ${priority}: ${count}`);
      });
      
      // Add task titles with their status and priority
      sections.push('\nTasks:');
      tasks.forEach(task => {
        sections.push(`- ${task.title} (Status: ${task.status}, Priority: ${task.priority})`);
      });
      
      sections.push('');
    }

    // Team Activity
    if (teamMembers && teamMembers.length > 0 && selectedFormat?.settings.teamProductivity) {
      sections.push('TEAM ACTIVITY');
      sections.push(`Total Team Members: ${teamMembers.length}`);
      teamMembers.forEach(member => {
        // Only include team members who are assigned to tasks for this agency
        const assignedTasks = taskAssignments?.filter(ta => ta.team_member_id === member.id) || [];
        if (assignedTasks.length > 0) {
          sections.push(`- ${member.name} (${member.job_title}) - Assigned to ${assignedTasks.length} task(s)`);
        }
      });
      sections.push('');
    }

    // Meeting Minutes
    if (meetingNotes.length > 0 && selectedFormat?.settings.meetingMinutes) {
      sections.push('MEETING MINUTES');
      
      // Add the selected meeting note
      meetingNotes.forEach((note) => {
        sections.push(`\nMeeting Note - ${new Date(note.created_at).toLocaleDateString()}`);
        sections.push(`Title: ${note.title}`);
        sections.push(`Content:\n${note.content}`);
        sections.push('---');
      });
            
      sections.push('');
    }

    // Additional Information
    if (additionalInfo && selectedFormat?.settings.additionalInfo) {
      sections.push('ADDITIONAL NOTES');
      sections.push(additionalInfo);
    }

    return sections.join('\n');
  };

  const downloadPDF = async (content: string) => {
    // Process logo first if needed, then generate the PDF
    if (selectedFormat?.settings.companyLogo && companyLogo) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          generatePDFWithContent(content, event.target.result as string);
        } else {
          generatePDFWithContent(content);
        }
      };
      reader.onerror = () => {
        console.error('Error reading logo file');
        generatePDFWithContent(content);
      };
      reader.readAsDataURL(companyLogo);
    } else {
      generatePDFWithContent(content);
    }
  };

  const generatePDFWithContent = (content: string, logoData?: string) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    const lineHeight = 8;
    
    // Track if we've already added report type and generation date
    let reportTypeAdded = false;
    let generationDateAdded = false;
    
    // Add header with better formatting
    let yPosition = margin;
    
    // Add report title if selected
    if (selectedFormat?.settings.reportType) {
      pdf.setFontSize(20); 
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(79, 70, 229); // Indigo color
      pdf.text(`${reportType.toUpperCase()} REPORT`, margin, yPosition);
      yPosition += 10;
      reportTypeAdded = true;
    }
    
    // Add company logo if available
    if (logoData && selectedFormat?.settings.companyLogo) {
      try {
        pdf.addImage(logoData, 'AUTO', pageWidth - margin - 40, margin, 40, 20);
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }
    
    // Add metadata
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0); // Reset text color to black
    
    // Only add preparedBy if selected in format
    if (selectedFormat?.settings.preparedBy) {
      pdf.text(`Prepared by: ${preparedBy}`, margin, yPosition);
      yPosition += 5;
    }
    
    // Only add generated date if selected in format
    if (selectedFormat?.settings.generatedOn) {
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += 5;
      generationDateAdded = true;
    }
    
    // Only add report type if selected in format and not already added
    if (selectedFormat?.settings.reportType && !reportTypeAdded) {
      pdf.text(`Report type: ${reportType}`, margin, yPosition);
      yPosition += 5;
    }
    
    // Parse content and add with better formatting
    const lines = content.split('\n');
    let y = yPosition + 10;
    
    lines.forEach(line => {
      if (y > pdf.internal.pageSize.height - margin) {
        pdf.addPage();
        y = margin;
      }
      
      // Check if this is a section header (all caps)
      if (line === line.toUpperCase() && line.trim() !== '') {
        // Skip if this is a duplicate of report type or generation date
        if ((line.includes('REPORT') && reportTypeAdded) || 
            (line.includes('Generated on:') && generationDateAdded)) {
          return; // Skip this line
        }
        
        // Format as a section header
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(79, 70, 229); // Indigo color
        
        // Add a light background for section headers
        pdf.setFillColor(240, 247, 255); // Light blue background
        pdf.rect(margin - 2, y - 6, pageWidth - (margin * 2) + 4, 10, 'F');
        
        pdf.text(line, margin, y);
        y += lineHeight + 2;
      } 
      // Check if this is a subsection (starts with -)
      else if (line.startsWith('-')) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0); // Black text
        pdf.text(line, margin + 5, y); // Indent bullet points
        y += lineHeight;
      }
      // Regular text
      else {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0); // Black text
        pdf.text(line, margin, y);
        y += lineHeight;
      }
    });
    
    pdf.save(`${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const downloadText = (content: string) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Generate Report
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Customer Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Customer Selection
              </h3>
              <select
                value={selectedAgencyId}
                onChange={(e) => setSelectedAgencyId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Select a customer</option>
                {agencies.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {agency.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Prepared By */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Prepared By
              </h3>
              <input
                type="text"
                value={preparedBy}
                onChange={(e) => setPreparedBy(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Your name"
              />
            </div>

            {/* Report Type Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Report Type
              </h3>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="weekly">Weekly Report</option>
                <option value="monthly">Monthly Report</option>
                <option value="quarterly">Quarterly Report</option>
                <option value="custom">Custom Report</option>
              </select>
            </div>

            {/* Format Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Format Options
              </h3>
              <div className="space-y-4">
                {reportType === 'custom' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Date Range
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                  </div>
                )}
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Template
                  </label>
                  <ReportFormatSelector onFormatSelect={setSelectedFormat} />
                </div>
              </div>
            </div>

            {/* Meeting Notes Selection (only shown when meetingMinutes is enabled) */}
            {selectedFormat?.settings.meetingMinutes && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Meeting Minutes
                </h3>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Select Meeting Notes
                  </label>
                  <div className="mt-1 space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2 dark:border-gray-600 dark:bg-gray-700">
                    {meetingNotes.length > 0 ? (
                      meetingNotes.map((note) => (
                        <div key={note.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`note-${note.id}`}
                            checked={selectedMeetingNoteIds.includes(note.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMeetingNoteIds([...selectedMeetingNoteIds, note.id]);
                              } else {
                                setSelectedMeetingNoteIds(selectedMeetingNoteIds.filter(id => id !== note.id));
                              }
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`note-${note.id}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-200">
                            {note.title} - {new Date(note.created_at).toLocaleDateString()}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {selectedAgencyId 
                          ? "No meeting notes found for this customer" 
                          : "No meeting notes found"}
                      </p>
                    )}
                  </div>
                  {meetingNotes.length === 0 && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                      {selectedAgencyId 
                        ? "No meeting notes found for this customer. Create meeting notes with this customer in the Notes section first." 
                        : "No meeting notes found. Create meeting notes in the Notes section first."}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Output Format Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Output Format
              </h3>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-indigo-600"
                    name="output-format"
                    value="pdf"
                    checked={outputFormat === 'pdf'}
                    onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'text')}
                  />
                  <span className="ml-2">PDF</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-indigo-600"
                    name="output-format"
                    value="text"
                    checked={outputFormat === 'text'}
                    onChange={(e) => setOutputFormat(e.target.value as 'pdf' | 'text')}
                  />
                  <span className="ml-2">Text</span>
                </label>
              </div>
            </div>

            {selectedFormat?.settings.companyLogo && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Upload Company Logo
                </h3>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCompanyLogo(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
            )}

            <div className="space-y-4">
              {selectedFormat?.settings.additionalInfo && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Additional Notes or Comments
                  </h3>
                  <textarea
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Add any additional information here..."
                  />
                </div>
              )}
              
              {selectedFormat?.settings.keyHighlights && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Key Highlights
                  </h3>
                  <textarea
                    value={keyHighlights}
                    onChange={(e) => setKeyHighlights(e.target.value)}
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Add key highlights here..."
                  />
                </div>
              )}
              
              {selectedFormat?.settings.achievements && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Achievements
                  </h3>
                  <textarea
                    value={achievements}
                    onChange={(e) => setAchievements(e.target.value)}
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Add achievements here..."
                  />
                </div>
              )}
              
              {selectedFormat?.settings.upcomingActions && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Upcoming Actions
                  </h3>
                  <textarea
                    value={upcomingActions}
                    onChange={(e) => setUpcomingActions(e.target.value)}
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Add upcoming actions here..."
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Generating...
                </>
              ) : outputFormat === 'pdf' ? (
                <>
                  <FilePdf className="-ml-1 mr-2 h-4 w-4" />
                  Generate PDF
                </>
              ) : (
                <>
                  <FileText className="-ml-1 mr-2 h-4 w-4" />
                  Generate Text
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}